import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Tab,
  Tabs,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import ArrowUpwardOutlinedIcon from '@mui/icons-material/ArrowUpwardOutlined';
import ArrowDownwardOutlinedIcon from '@mui/icons-material/ArrowDownwardOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { pdf } from '@react-pdf/renderer';
import { v4 as uuidV4 } from 'uuid';

import Breadcrumbs from 'src/Lca/components/Breadcrumbs';
import LcaLayout from 'src/Lca/components/layout';
import PageLoading from 'src/components/PageLoading';
import { useLcaOrganization } from 'src/common/hooks';
import { usePlatform } from 'src/redux-file/hooks';
import { client } from 'src/utils/api-client';
import {
  useCreateGeneratedReport,
  useGeneratedReports,
  useLcaDiagram,
  useProductImpacts,
  useReportDraft,
  useUpsertReportDraft,
} from 'src/Lca/hooks';
import { getLcaApiErrorMessage } from 'src/Lca/apiErrors';

import LcaReportPdfDocument from './pdf';
import CustomRichTextEditor from './CustomRichTextEditor';
import {
  createDefaultReportConfig,
  DEFAULT_REPORT_ACCENT_COLOR,
  getDefaultReportTitle,
  getReportBuilderStepLabel,
  getReportSectionDescription,
  getReportSectionTitle,
  getReportTypeDescription,
  getReportTypeLabel,
  ONE_PAGER_REPORT_BUILDER_STEPS,
  REPORT_BUILDER_STEPS,
  REPORT_SECTIONS,
  REPORT_TYPE_OPTIONS,
} from './config';
import { clearDraftConfig, getDraftConfig } from './storage';
import {
  getLatestSeriesId,
  getLatestSeriesVersion,
  mapGeneratedReport,
} from './history';
import {
  applyAudiencePreset,
  applyReportLanguage,
  buildReadinessMetrics,
  buildAutoInterpretation,
  buildPreviewData,
  buildSectionPreviews,
  enforceDataDrivenSectionRules,
  estimatePages,
  formatNumber,
  getFlowchartStageSummaries,
  getLifecycleBoundaryLabel,
  getSection,
  getSectionCustomContent,
  getSelectedImpactIds,
  getSelectedImpacts,
  getWeightedRecycledShare,
  hasBlockingIssues,
  hasDurabilityData,
  hasMaterialsCircularityData,
  hasMethodologyData,
  htmlToPlainText,
  moveImage,
  replaceSection,
  resizeImageFile,
  resolveReportLanguage,
  sanitizeOnePagerConfig,
  sanitizeRichTextHtml,
  sha256Hex,
  svgToPngFile,
  updateSectionCustomContent,
  validateReportConfig,
} from './utils';
import {
  DetailLevel,
  OutputFormat,
  ReportConfig,
  ReportDraftSnapshot,
  ReportProductData,
  ReportSectionImage,
  ReportSectionImageLayout,
  ReportValidationIssue,
  ReportVersionRecord,
  SaveMode,
  SectionId,
} from './types';

type OrganizationImageUploadResponse = {
  id: string;
  name: string;
  size: number;
  content_type: string;
  url: string;
};

const MAX_SECTION_IMAGES = 5;
const COVER_IMAGE_ACCEPT =
  'image/png,image/jpeg,image/webp,image/gif,image/bmp,image/svg+xml';
const LOGO_IMAGE_ACCEPT = 'image/png,image/svg+xml';

const wait = (ms: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const toAbsoluteUrl = (url: string) => {
  if (url.startsWith('http')) return url;
  return `${window.location.origin}${url}`;
};

const sanitizeFilename = (name: string) =>
  name.replace(/[^a-zA-Z0-9._\- ]+/g, '').trim() || 'LCA Report';

const getSectionImageWidth = (layout: ReportSectionImageLayout) => {
  if (layout === 'full') return '100%';
  if (layout === 'medium') return '62%';
  return '32%';
};

const getSectionImageMinWidth = (layout: ReportSectionImageLayout) => {
  if (layout === 'small') return 160;
  if (layout === 'medium') return 240;
  return '100%';
};

const getApiDetailMessage = (error: unknown) => {
  const detail = (
    error as {
      response?: { data?: { detail?: string | { message?: string } } };
    }
  ).response?.data?.detail;

  if (typeof detail === 'string') return detail;
  if (
    detail &&
    typeof detail === 'object' &&
    typeof detail.message === 'string'
  ) {
    return detail.message;
  }

  return null;
};

const downloadBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const downloadFromUrl = async (url: string, filename: string) => {
  const response = await fetch(toAbsoluteUrl(url), {
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(`Failed to download ${filename}`);
  }
  const blob = await response.blob();
  downloadBlob(blob, filename);
};

const getVersionLabel = (record: ReportVersionRecord) =>
  `v${record.version} (${record.generatedAt.slice(0, 10)})`;

const ACCENT_COLOR_PRESETS = [
  { labelKey: 'accentPresets.envirlyTeal', value: '#165D5C' },
  { labelKey: 'accentPresets.forest', value: '#2F6B3B' },
  { labelKey: 'accentPresets.ocean', value: '#1F6E8C' },
  { labelKey: 'accentPresets.terracotta', value: '#B85C38' },
  { labelKey: 'accentPresets.slate', value: '#425466' },
] as const;

const mergeReportConfigWithFallback = (
  fallbackConfig: ReportConfig,
  draftConfig: Partial<ReportConfig>
): ReportConfig => ({
  ...fallbackConfig,
  ...draftConfig,
  branding: {
    ...fallbackConfig.branding,
    ...draftConfig.branding,
    accentColor:
      draftConfig.branding?.accentColor || fallbackConfig.branding.accentColor,
    // A draft saved before a logo existed (uploaded, or set on the
    // organization) has logoUrl/logoAssetId as null — fall back to the
    // current default instead of leaving it stuck on text-only branding.
    logoUrl: draftConfig.branding?.logoUrl || fallbackConfig.branding.logoUrl,
    logoAssetId:
      draftConfig.branding?.logoAssetId || fallbackConfig.branding.logoAssetId,
  },
  layout: {
    ...fallbackConfig.layout,
    ...draftConfig.layout,
    numberFormat: {
      ...fallbackConfig.layout.numberFormat,
      ...draftConfig.layout?.numberFormat,
    },
    watermark: {
      ...fallbackConfig.layout.watermark,
      ...draftConfig.layout?.watermark,
    },
    footerOptions: {
      ...fallbackConfig.layout.footerOptions,
      ...draftConfig.layout?.footerOptions,
    },
  },
  output: {
    ...fallbackConfig.output,
    ...draftConfig.output,
  },
  audit: {
    ...fallbackConfig.audit,
    ...draftConfig.audit,
    datasetVersions: {
      ...fallbackConfig.audit.datasetVersions,
      ...draftConfig.audit?.datasetVersions,
    },
  },
  sections: Array.isArray(draftConfig.sections)
    ? draftConfig.sections
    : fallbackConfig.sections,
});

export default function LcaReportBuilder() {
  const { t, i18n } = useTranslation('lca', { keyPrefix: 'reportBuilder' });
  const toastText = React.useCallback(
    (key: string, options?: Record<string, unknown>) =>
      t(key, options) as string,
    [t]
  );
  const { id } = useParams<{ id: string }>();
  const productId = Number(id);
  const organization = useLcaOrganization();
  const { user } = usePlatform();
  const navigate = useNavigate();

  const [activeStep, setActiveStep] = React.useState(0);
  const [pageTab, setPageTab] = React.useState<
    'generate' | 'generated-reports'
  >('generate');
  const [config, setConfig] = React.useState<ReportConfig | null>(null);
  const [selectedAreaId, setSelectedAreaId] = React.useState<
    number | undefined
  >(undefined);
  const [saveMode, setSaveMode] = React.useState<SaveMode>('new-version');
  const [isUploading, setIsUploading] = React.useState(false);
  const [isGenerating, setIsGenerating] = React.useState(false);
  const [generationStage, setGenerationStage] = React.useState(0);
  const [successRecord, setSuccessRecord] =
    React.useState<ReportVersionRecord | null>(null);

  const sectionRefs = React.useRef<Record<string, HTMLDivElement | null>>({});
  const isOnePager = config?.reportType === 'lca-one-pager';
  const builderSteps = isOnePager
    ? ONE_PAGER_REPORT_BUILDER_STEPS
    : REPORT_BUILDER_STEPS;
  const lastStepIndex = builderSteps.length - 1;
  const generationStages = React.useMemo(
    () => [
      t('generationStages.preparingData'),
      t('generationStages.renderingCharts'),
      t('generationStages.buildingPdf'),
      t('generationStages.finalizing'),
    ],
    [t]
  );
  const uiContentLanguage = (
    i18n.resolvedLanguage ||
    i18n.language ||
    'en'
  ).split('-')[0];
  let uiLanguage: ReportConfig['language'] = 'en';
  if (i18n.language.startsWith('pl')) {
    uiLanguage = 'pl';
  } else if (i18n.language.startsWith('sv')) {
    uiLanguage = 'sv';
  }

  const { data: product, isLoading: isLoadingProduct } = useQuery(
    ['lca', 'product', 'report-builder', productId],
    () =>
      client
        .get<ReportProductData>(`/web/lca/products/${productId}`)
        .then((response) => response.data),
    { enabled: Number.isFinite(productId) }
  );

  const {
    data: impactData,
    isLoading: isLoadingImpacts,
    error: impactError,
  } = useProductImpacts(productId);
  const { data: diagramData, isLoading: isLoadingDiagram } =
    useLcaDiagram(productId);
  const { reportDraft, isLoading: isLoadingReportDraft } =
    useReportDraft(productId);
  const { generatedReports, isLoading: isLoadingGeneratedReports } =
    useGeneratedReports(productId);
  const createGeneratedReport = useCreateGeneratedReport(productId);
  const upsertReportDraftMutation = useUpsertReportDraft(productId);

  const history = React.useMemo(
    () => generatedReports.map(mapGeneratedReport),
    [generatedReports]
  );

  const previewData = React.useMemo(
    () => buildPreviewData(impactData || [], selectedAreaId),
    [impactData, selectedAreaId]
  );
  const canWrite = organization.can_write_data;
  const hasInitializedDraftRef = React.useRef(false);
  const autosaveTimerRef = React.useRef<number | null>(null);
  const lastSavedDraftRef = React.useRef<string | null>(null);
  const pendingDraftRef = React.useRef<string | null>(null);
  const hasMigratedLegacyDraftRef = React.useRef(false);
  const upsertReportDraftMutateAsyncRef = React.useRef(
    upsertReportDraftMutation.mutateAsync
  );

  React.useEffect(() => {
    upsertReportDraftMutateAsyncRef.current =
      upsertReportDraftMutation.mutateAsync;
  }, [upsertReportDraftMutation.mutateAsync]);

  React.useEffect(() => {
    if (!product || !organization || !user || config || isLoadingReportDraft)
      return;

    const fallbackConfig = createDefaultReportConfig({
      productId: product.id,
      organizationId: organization.id,
      productName: product.name,
      organizationName: organization.name,
      organizationLogo: organization.logo
        ? {
            id: organization.logo.id,
            url: toAbsoluteUrl(organization.logo.url),
          }
        : null,
      userId: String(user.id),
      language: uiLanguage,
    });

    if (product.ecoinvent_version) {
      fallbackConfig.audit.datasetVersions.ecoinvent =
        product.ecoinvent_version;
    }

    const legacyDraftConfig = getDraftConfig(product.id);
    const backendDraftConfig = reportDraft?.config_snapshot as
      Partial<ReportConfig> | undefined;
    const initialDraftConfig = backendDraftConfig || legacyDraftConfig || null;
    const mergedConfig = initialDraftConfig
      ? mergeReportConfigWithFallback(fallbackConfig, initialDraftConfig)
      : fallbackConfig;

    const nextConfig = resolveReportLanguage(mergedConfig, uiLanguage);

    setConfig(nextConfig);
    setSelectedAreaId(reportDraft?.selected_area_id || undefined);
    hasInitializedDraftRef.current = true;
    pendingDraftRef.current = null;
    lastSavedDraftRef.current = JSON.stringify({
      configSnapshot: sanitizeOnePagerConfig(nextConfig),
      selectedAreaId: reportDraft?.selected_area_id || null,
    } satisfies ReportDraftSnapshot);

    if (
      !reportDraft &&
      legacyDraftConfig &&
      canWrite &&
      !hasMigratedLegacyDraftRef.current
    ) {
      hasMigratedLegacyDraftRef.current = true;
      upsertReportDraftMutateAsyncRef
        .current({
          config_snapshot: sanitizeOnePagerConfig(nextConfig),
          selected_area_id: null,
        })
        .then(() => {
          clearDraftConfig(product.id);
        })
        .catch(() => {
          hasMigratedLegacyDraftRef.current = false;
        });
    } else if (reportDraft) {
      clearDraftConfig(product.id);
    }
  }, [
    canWrite,
    config,
    isLoadingReportDraft,
    organization,
    product,
    reportDraft,
    uiLanguage,
    user,
  ]);

  // The interface language can be switched while the builder is open, so a
  // report that was never pinned to a language follows it.
  React.useEffect(() => {
    if (!config) return;
    const nextConfig = resolveReportLanguage(config, uiLanguage);
    if (nextConfig !== config) setConfig(nextConfig);
  }, [config, uiLanguage]);

  React.useEffect(() => {
    if (!config) return;

    const nextConfig = enforceDataDrivenSectionRules(config, previewData);
    if (
      JSON.stringify(nextConfig.sections) !== JSON.stringify(config.sections)
    ) {
      setConfig(nextConfig);
      toast.info(toastText('toasts.scenariosDisabled'));
    }
  }, [config, previewData, t]);

  React.useEffect(() => {
    if (!config || config.reportType !== 'lca-one-pager') return;

    const nextConfig = sanitizeOnePagerConfig(config);
    if (JSON.stringify(nextConfig) !== JSON.stringify(config)) {
      setConfig(nextConfig);
    }
  }, [config]);

  const validationIssues = React.useMemo(() => {
    if (!config) return [];
    const issues: ReportValidationIssue[] = validateReportConfig(
      sanitizeOnePagerConfig(config),
      product || null,
      previewData,
      canWrite
    );
    const impactErrorMessage = getLcaApiErrorMessage(impactError, (key) =>
      i18n.t(`lca:${key}`)
    );
    if (impactErrorMessage) {
      return [
        ...issues,
        {
          id: 'IMPACT_DATA_BROKEN_SUB_PRODUCT_REFERENCE',
          blocking: true,
          message: impactErrorMessage,
        },
      ];
    }
    return issues;
  }, [
    canWrite,
    config,
    previewData,
    product,
    impactError,
    i18n,
    i18n.language,
  ]);

  const hasBlockingValidation = hasBlockingIssues(validationIssues);

  React.useEffect(() => {
    if (
      !config ||
      !product ||
      !canWrite ||
      !hasInitializedDraftRef.current ||
      isLoadingReportDraft
    ) {
      return undefined;
    }

    const snapshot: ReportDraftSnapshot = {
      configSnapshot: sanitizeOnePagerConfig(config),
      selectedAreaId: selectedAreaId || null,
    };
    const serializedSnapshot = JSON.stringify(snapshot);

    if (
      serializedSnapshot === lastSavedDraftRef.current ||
      serializedSnapshot === pendingDraftRef.current
    ) {
      return undefined;
    }

    if (autosaveTimerRef.current) {
      window.clearTimeout(autosaveTimerRef.current);
    }

    autosaveTimerRef.current = window.setTimeout(() => {
      pendingDraftRef.current = serializedSnapshot;

      upsertReportDraftMutateAsyncRef
        .current({
          config_snapshot: snapshot.configSnapshot,
          selected_area_id: snapshot.selectedAreaId,
        })
        .then(() => {
          lastSavedDraftRef.current = serializedSnapshot;
          if (pendingDraftRef.current === serializedSnapshot) {
            pendingDraftRef.current = null;
          }
        })
        .catch(() => {
          if (pendingDraftRef.current === serializedSnapshot) {
            pendingDraftRef.current = null;
          }
          toast.warn(toastText('toasts.reportDraftSaveFailed'));
        });
    }, 1000);

    return () => {
      if (autosaveTimerRef.current) {
        window.clearTimeout(autosaveTimerRef.current);
      }
    };
  }, [
    canWrite,
    config,
    isLoadingReportDraft,
    product,
    selectedAreaId,
    toastText,
  ]);

  React.useEffect(() => {
    setActiveStep((currentStep) => Math.min(currentStep, lastStepIndex));
  }, [lastStepIndex]);

  const sectionPreviews = React.useMemo(
    () => (config ? buildSectionPreviews(sanitizeOnePagerConfig(config)) : []),
    [config, i18n.language]
  );

  const estimatedPages = React.useMemo(
    () => (config ? estimatePages(sanitizeOnePagerConfig(config)) : 0),
    [config]
  );

  const readinessMetrics = React.useMemo(
    () => (product ? buildReadinessMetrics(product, previewData) : []),
    [previewData, product, i18n.language]
  );

  const onUploadImage = async (file: File, target: 'logo' | 'cover') => {
    if (!config) return;

    const isPng = file.type === 'image/png';
    const isRasterImage =
      file.type === 'image/jpeg' ||
      file.type === 'image/webp' ||
      file.type === 'image/gif' ||
      file.type === 'image/bmp';
    const isSvg = file.type === 'image/svg+xml' || file.name.endsWith('.svg');

    const isValidLogo = isPng || isSvg;
    const isValidCover = isPng || isRasterImage || isSvg;

    if (
      (target === 'logo' && !isValidLogo) ||
      (target === 'cover' && !isValidCover)
    ) {
      toast.error(
        target === 'logo'
          ? toastText('toasts.logoInvalidType')
          : toastText('toasts.coverInvalidType')
      );
      return;
    }

    setIsUploading(true);

    try {
      const normalizedFile = isSvg
        ? await svgToPngFile(file)
        : await resizeImageFile(file);

      const formData = new FormData();
      formData.append('file', normalizedFile);

      const { data } = await client.post<OrganizationImageUploadResponse>(
        `/web/organizations/${organization.id}/images`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );

      const url = toAbsoluteUrl(data.url);
      if (target === 'logo') {
        setConfig({
          ...config,
          branding: {
            ...config.branding,
            logoAssetId: data.id,
            logoUrl: url,
          },
        });
      } else {
        setConfig({
          ...config,
          branding: {
            ...config.branding,
            coverAssetId: data.id,
            coverUrl: url,
            coverStyle: 'user',
          },
        });
      }

      toast.success(
        target === 'logo'
          ? toastText('toasts.logoUploaded')
          : toastText('toasts.coverUploaded')
      );
    } catch (error) {
      toast.error(
        target === 'logo'
          ? toastText('toasts.logoUploadFailed')
          : toastText('toasts.coverUploadFailed')
      );
    } finally {
      setIsUploading(false);
    }
  };

  const onUseOrganizationLogo = () => {
    if (!config || !organization.logo) return;

    setConfig({
      ...config,
      branding: {
        ...config.branding,
        logoAssetId: organization.logo.id,
        logoUrl: toAbsoluteUrl(organization.logo.url),
      },
    });
  };

  const onSectionToggle = (sectionId: SectionId, enabled: boolean) => {
    if (!config) return;

    const isAuditRequired =
      config.audience === 'audit-ready' &&
      ['methodology-standards', 'disclaimer-audit-trail'].includes(sectionId);

    if (isAuditRequired && !enabled) {
      toast.warn(toastText('toasts.auditSectionRequired'));
      return;
    }

    setConfig(replaceSection(config, sectionId, { enabled }));
  };

  const onSectionDetailLevel = (
    sectionId: SectionId,
    detailLevel: DetailLevel
  ) => {
    if (!config) return;
    setConfig(replaceSection(config, sectionId, { detailLevel }));
  };

  const onSectionCustomHtmlChange = (sectionId: SectionId, html: string) => {
    setConfig((currentConfig) => {
      if (!currentConfig) return currentConfig;

      return updateSectionCustomContent(currentConfig, sectionId, {
        html: sanitizeRichTextHtml(html),
      });
    });
  };

  const onSectionOptionsChange = (
    sectionId: SectionId,
    patch: Record<string, unknown>
  ) => {
    setConfig((currentConfig) => {
      if (!currentConfig) return currentConfig;

      const currentSection = getSection(currentConfig, sectionId);
      return replaceSection(currentConfig, sectionId, {
        options: {
          ...(currentSection?.options || {}),
          ...patch,
        },
      });
    });
  };

  const onSectionImageUpload = async (sectionId: SectionId, file: File) => {
    if (!config) return;

    const currentSection = getSection(config, sectionId);
    const currentCustomContent = getSectionCustomContent(currentSection);
    if (currentCustomContent.images.length >= MAX_SECTION_IMAGES) {
      toast.warn(
        toastText('toasts.maxSectionImages', { count: MAX_SECTION_IMAGES })
      );
      return;
    }

    try {
      setIsUploading(true);

      let normalizedFile = file;
      if (file.type === 'image/svg+xml') {
        normalizedFile = await svgToPngFile(file);
      } else {
        normalizedFile = await resizeImageFile(file);
      }

      const formData = new FormData();
      formData.append('file', normalizedFile);

      const { data } = await client.post<OrganizationImageUploadResponse>(
        `/web/organizations/${organization.id}/images`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );

      const nextImage: ReportSectionImage = {
        id: uuidV4(),
        assetId: data.id,
        name: data.name,
        size: data.size,
        contentType: data.content_type,
        url: toAbsoluteUrl(data.url),
        caption: '',
        alt: data.name,
        layout: 'medium',
      };

      setConfig((currentConfig) => {
        if (!currentConfig) return currentConfig;

        const nextSection = getSection(currentConfig, sectionId);
        const nextCustomContent = getSectionCustomContent(nextSection);
        return updateSectionCustomContent(currentConfig, sectionId, {
          images: [...nextCustomContent.images, nextImage],
        });
      });

      toast.success(toastText('toasts.sectionImageUploaded'));
    } catch {
      toast.error(toastText('toasts.sectionImageUploadFailed'));
    } finally {
      setIsUploading(false);
    }
  };

  const onSectionImageChange = (
    sectionId: SectionId,
    imageId: string,
    patch: Partial<ReportSectionImage>
  ) => {
    setConfig((currentConfig) => {
      if (!currentConfig) return currentConfig;

      const currentSection = getSection(currentConfig, sectionId);
      const currentCustomContent = getSectionCustomContent(currentSection);
      const images = currentCustomContent.images.map((image) =>
        image.id === imageId ? { ...image, ...patch } : image
      );

      return updateSectionCustomContent(currentConfig, sectionId, { images });
    });
  };

  const onSectionImageDelete = (sectionId: SectionId, imageId: string) => {
    setConfig((currentConfig) => {
      if (!currentConfig) return currentConfig;

      const currentSection = getSection(currentConfig, sectionId);
      const currentCustomContent = getSectionCustomContent(currentSection);
      const images = currentCustomContent.images.filter(
        (image) => image.id !== imageId
      );

      return updateSectionCustomContent(currentConfig, sectionId, { images });
    });
  };

  const onSectionImageMove = (
    sectionId: SectionId,
    imageId: string,
    direction: 'up' | 'down'
  ) => {
    setConfig((currentConfig) => {
      if (!currentConfig) return currentConfig;

      const currentSection = getSection(currentConfig, sectionId);
      const currentCustomContent = getSectionCustomContent(currentSection);
      const imageIndex = currentCustomContent.images.findIndex(
        (image) => image.id === imageId
      );

      if (imageIndex < 0) return currentConfig;

      const targetIndex = direction === 'up' ? imageIndex - 1 : imageIndex + 1;
      const images = moveImage(
        currentCustomContent.images,
        imageIndex,
        targetIndex
      );

      return updateSectionCustomContent(currentConfig, sectionId, { images });
    });
  };

  const onSelectAllSections = (enabled: boolean) => {
    if (!config) return;

    setConfig({
      ...config,
      sections: config.sections.map((section) => {
        const isAuditRequired =
          config.audience === 'audit-ready' &&
          ['methodology-standards', 'disclaimer-audit-trail'].includes(
            section.id
          );
        if (isAuditRequired) return { ...section, enabled: true };
        return { ...section, enabled };
      }),
    });
  };

  const onAudienceChange = (audience: ReportConfig['audience']) => {
    if (!config) return;
    setConfig(applyAudiencePreset(config, audience));
  };

  const onReportTypeChange = (reportType: ReportConfig['reportType']) => {
    if (!config) return;

    const defaultTitles = new Set(
      REPORT_TYPE_OPTIONS.map((option) =>
        getDefaultReportTitle(option.id, config.language)
      )
    );
    const nextDefaultTitle = getDefaultReportTitle(reportType, config.language);
    const shouldUpdateTitle = defaultTitles.has(config.branding.reportTitle);

    setConfig(
      sanitizeOnePagerConfig({
        ...config,
        reportType,
        branding: {
          ...config.branding,
          reportTitle: shouldUpdateTitle
            ? nextDefaultTitle
            : config.branding.reportTitle,
        },
        output: {
          ...config.output,
          formats:
            reportType === 'lca-one-pager'
              ? config.output.formats.filter((format) => format !== 'docx')
              : config.output.formats,
        },
      })
    );
  };

  const onChangeFormats = (format: OutputFormat, checked: boolean) => {
    if (!config) return;

    const existing = new Set(config.output.formats);
    if (checked) existing.add(format);
    else existing.delete(format);

    const formats = Array.from(existing);
    setConfig({
      ...config,
      output: {
        ...config.output,
        formats,
      },
    });
  };

  const buildConfigForGeneration = async (): Promise<ReportConfig | null> => {
    if (!config || !product) return null;

    const seriesId =
      saveMode === 'new-version'
        ? getLatestSeriesId(history) || config.seriesId
        : uuidV4();

    const nextReportId = uuidV4();
    const generatedAt = new Date().toISOString();

    const baseConfig: ReportConfig = sanitizeOnePagerConfig({
      ...config,
      reportId: nextReportId,
      seriesId,
      audit: {
        ...config.audit,
        generatedAt,
        generatedByUserId: String(user?.id || 'unknown'),
      },
    });

    const hash = await sha256Hex(JSON.stringify(baseConfig));

    return {
      ...baseConfig,
      audit: {
        ...baseConfig.audit,
        hash,
      },
      output: {
        ...baseConfig.output,
        reportName:
          baseConfig.output.reportName ||
          (i18n.t('lca:reportBuilder.defaults.reportName', {
            lng: baseConfig.language,
            productName: product.name,
            date: generatedAt.slice(0, 10),
          }) as string),
      },
      sections: baseConfig.sections.map((section) => {
        if (section.id === 'interpretation' && section.enabled) {
          return {
            ...section,
            options: {
              ...section.options,
              narrative: buildAutoInterpretation(
                previewData,
                baseConfig.language
              ),
            },
          };
        }
        if (section.id === 'scenarios' && !previewData.hasScenarioData) {
          return {
            ...section,
            enabled: false,
            options: {
              ...section.options,
              autoDisabledReason: i18n.t(
                'lca:reportBuilder.validation.scenariosUnavailable',
                {
                  lng: baseConfig.language,
                }
              ) as string,
            },
          };
        }
        return section;
      }),
    } as ReportConfig;
  };

  const createPdfBlob = async (
    nextConfig: ReportConfig,
    isDraft: boolean
  ): Promise<Blob> => {
    const pdfIssues = validateReportConfig(
      sanitizeOnePagerConfig(nextConfig),
      product || null,
      previewData,
      canWrite,
      nextConfig.language
    );
    const document = (
      <LcaReportPdfDocument
        config={nextConfig}
        previewData={previewData}
        diagramData={diagramData || null}
        sections={buildSectionPreviews(nextConfig, nextConfig.language)}
        issues={pdfIssues}
        product={product || null}
        isDraft={isDraft}
      />
    );

    return pdf(document).toBlob();
  };

  const downloadPdf = async (nextConfig: ReportConfig, isDraft: boolean) => {
    const blob = await createPdfBlob(nextConfig, isDraft);
    const filename = `${sanitizeFilename(nextConfig.output.reportName)}${
      isDraft ? ' - draft' : ''
    }.pdf`;
    downloadBlob(blob, filename);
  };

  const onDownloadDraft = async () => {
    if (!config) return;

    try {
      const draftConfig: ReportConfig = {
        ...config,
        layout: {
          ...config.layout,
          watermark: {
            ...config.layout.watermark,
            enabled: true,
          },
        },
      };

      await downloadPdf(draftConfig, true);
      toast.success(toastText('toasts.draftDownloaded'));
    } catch {
      toast.error(toastText('toasts.draftDownloadFailed'));
    }
  };

  const onGenerate = async () => {
    if (!config || !product || isGenerating) return;
    if (hasBlockingValidation) {
      toast.error(toastText('toasts.resolveBlockingIssues'));
      return;
    }

    setIsGenerating(true);
    setGenerationStage(0);
    setSuccessRecord(null);

    try {
      setGenerationStage(0);
      await wait(350);
      const nextConfig = await buildConfigForGeneration();
      if (!nextConfig)
        throw new Error(toastText('errors.missingGenerationConfig'));

      setGenerationStage(1);
      await wait(450);

      const version = getLatestSeriesVersion(history, nextConfig.seriesId) + 1;
      const pdfFilename = `${sanitizeFilename(nextConfig.output.reportName)}.pdf`;
      const pdfBlob = await createPdfBlob(nextConfig, false);

      setGenerationStage(2);

      const formData = new FormData();
      formData.append(
        'pdf_file',
        new File([pdfBlob], pdfFilename, { type: 'application/pdf' })
      );
      formData.append('report_id', nextConfig.reportId);
      formData.append('series_id', nextConfig.seriesId);
      formData.append('version', String(version));
      formData.append('report_name', nextConfig.output.reportName);
      formData.append('generated_at', nextConfig.audit.generatedAt);
      formData.append(
        'warnings_count',
        String(validationIssues.filter((issue) => !issue.blocking).length)
      );
      if (selectedAreaId) {
        formData.append('selected_area_id', String(selectedAreaId));
      }
      formData.append('config_snapshot', JSON.stringify(nextConfig));
      formData.append('hash', nextConfig.audit.hash);
      formData.append('formats', JSON.stringify(nextConfig.output.formats));

      const savedReport = mapGeneratedReport(
        await createGeneratedReport.mutateAsync(formData)
      );

      setGenerationStage(3);
      await wait(400);

      setConfig(nextConfig);
      setSuccessRecord(savedReport);
      downloadBlob(pdfBlob, pdfFilename);

      const docxArtifact = savedReport.artifacts.find(
        (artifact) => artifact.format === 'docx'
      );
      if (docxArtifact) {
        await downloadFromUrl(docxArtifact.url, docxArtifact.name);
      }

      toast.success(toastText('toasts.reportGenerated'));
    } catch (error) {
      toast.error(
        getLcaApiErrorMessage(error, (key) => i18n.t(`lca:${key}`)) ||
          getApiDetailMessage(error) ||
          toastText('toasts.reportGenerationFailed')
      );
    } finally {
      setIsGenerating(false);
    }
  };

  const onShare = async () => {
    const url = `${window.location.origin}/lca/products/${productId}/report-builder`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(toastText('toasts.shareLinkCopied'));
    } catch {
      toast.error(toastText('toasts.shareLinkCopyFailed'));
    }
  };

  if (
    isLoadingProduct ||
    isLoadingImpacts ||
    isLoadingDiagram ||
    isLoadingReportDraft ||
    !config ||
    !product
  ) {
    return <PageLoading />;
  }

  const breadcrumbs = [
    { name: t('breadcrumbs.project'), url: `/lca/products/${product.id}` },
    {
      name: t('breadcrumbs.reports'),
      url: `/lca/products/${product.id}/report-builder`,
    },
    {
      name:
        pageTab === 'generate'
          ? t('tabs.generate')
          : t('tabs.generatedReports'),
      url: '',
    },
  ];

  const enabledSectionsCount = config.sections.filter(
    (section) => section.enabled
  ).length;
  const getWarningsLabel = (count: number) => {
    const pluralCategory = new Intl.PluralRules(i18n.language).select(count);
    return t(`history.warnings_${pluralCategory}`, {
      count,
      defaultValue: t('history.warnings_other', { count }),
    });
  };

  let generatedReportsContent: React.ReactNode;
  if (isLoadingGeneratedReports) {
    generatedReportsContent = <LinearProgress />;
  } else if (history.length) {
    generatedReportsContent = (
      <Stack spacing={1.5}>
        {history.map((entry) => {
          return (
            <Card key={entry.reportId} variant="outlined">
              <CardContent>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  gap={2}
                  flexWrap="wrap"
                >
                  <Stack spacing={0.5}>
                    <Typography variant="h6">{entry.reportName}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('history.generatedBy', {
                        versionLabel: getVersionLabel(entry),
                        name: entry.generatedByName,
                      })}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {getWarningsLabel(entry.warningsCount)}
                    </Typography>
                  </Stack>

                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {entry.artifacts.map((artifact) => (
                      <Button
                        key={artifact.id}
                        variant="secondary"
                        startIcon={<DownloadOutlinedIcon />}
                        onClick={() =>
                          downloadFromUrl(artifact.url, artifact.name)
                        }
                      >
                        {artifact.format.toUpperCase()}
                      </Button>
                    ))}
                  </Stack>
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Stack>
    );
  } else {
    generatedReportsContent = (
      <Alert severity="info">{t('history.empty')}</Alert>
    );
  }

  return (
    <LcaLayout>
      <Breadcrumbs data={breadcrumbs} />

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mt={4}
        mb={3}
      >
        <Typography variant="h1">{t('title')}</Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="secondary"
            onClick={() => navigate(`/lca/products/${product.id}`)}
          >
            {t('actions.cancel')}
          </Button>
          {pageTab === 'generated-reports' ? (
            <Button variant="primary" onClick={() => setPageTab('generate')}>
              {t('actions.goToGenerate')}
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={() => setActiveStep(lastStepIndex)}
              disabled={hasBlockingValidation}
            >
              {t('actions.goToGenerate')}
            </Button>
          )}
        </Stack>
      </Box>

      <Tabs
        value={pageTab}
        onChange={(_event, value) => setPageTab(value)}
        sx={{ mb: 3 }}
      >
        <Tab value="generate" label={t('tabs.generate')} />
        <Tab value="generated-reports" label={t('tabs.generatedReports')} />
      </Tabs>

      {pageTab === 'generate' ? (
        <>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={2} alignItems="flex-start">
                <Grid item xs={12} md={6} lg={5}>
                  <FormControl fullWidth>
                    <InputLabel>{t('branding.reportType')}</InputLabel>
                    <Select
                      label={t('branding.reportType')}
                      value={config.reportType}
                      onChange={(event) =>
                        onReportTypeChange(
                          event.target.value as ReportConfig['reportType']
                        )
                      }
                    >
                      {REPORT_TYPE_OPTIONS.map((option) => (
                        <MenuItem key={option.id} value={option.id}>
                          {getReportTypeLabel(option.id, uiContentLanguage)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Alert severity="info">
                    {getReportTypeDescription(
                      config.reportType,
                      uiContentLanguage
                    )}
                  </Alert>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Grid container spacing={3} alignItems="flex-start">
            <Grid item xs={12} md={3} lg={2.6}>
              <Card>
                <CardContent>
                  <Stepper activeStep={activeStep} orientation="vertical">
                    {builderSteps.map((label) => (
                      <Step key={label}>
                        <StepLabel>
                          {getReportBuilderStepLabel(label, uiContentLanguage)}
                        </StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6} lg={6.8}>
              <Card>
                <CardContent>
                  {activeStep === 0 && !isOnePager && (
                    <Stack spacing={2.5}>
                      <Typography variant="h4">
                        {t('steps.brandCover')}
                      </Typography>

                      <Box display="flex" gap={2} flexWrap="wrap">
                        <Button
                          variant="secondary"
                          component="label"
                          startIcon={<UploadFileOutlinedIcon />}
                          disabled={isUploading}
                        >
                          {t('branding.uploadLogo')}
                          <input
                            hidden
                            type="file"
                            accept={LOGO_IMAGE_ACCEPT}
                            onChange={(event) => {
                              const file = event.target.files?.[0];
                              if (file) onUploadImage(file, 'logo');
                            }}
                          />
                        </Button>

                        <Tooltip
                          title={
                            organization.logo
                              ? ''
                              : t('branding.noOrganizationLogo')
                          }
                        >
                          <span>
                            <Button
                              variant="secondary"
                              startIcon={<SyncOutlinedIcon />}
                              disabled={isUploading || !organization.logo}
                              onClick={onUseOrganizationLogo}
                            >
                              {t('branding.useOrganizationLogo')}
                            </Button>
                          </span>
                        </Tooltip>

                        <Button
                          variant="secondary"
                          component="label"
                          startIcon={<UploadFileOutlinedIcon />}
                          disabled={isUploading}
                        >
                          {t('branding.uploadCover')}
                          <input
                            hidden
                            type="file"
                            accept={COVER_IMAGE_ACCEPT}
                            onChange={(event) => {
                              const file = event.target.files?.[0];
                              if (file) onUploadImage(file, 'cover');
                            }}
                          />
                        </Button>
                      </Box>

                      {config.branding.logoUrl && (
                        <Box
                          sx={{
                            p: 2,
                            border: '1px solid #ddd',
                            borderRadius: 2,
                            width: 'fit-content',
                          }}
                        >
                          <img
                            src={config.branding.logoUrl}
                            alt="logo"
                            style={{
                              maxWidth: 180,
                              maxHeight: 70,
                              objectFit: 'contain',
                            }}
                          />
                        </Box>
                      )}

                      <TextField
                        label={t('branding.companyName')}
                        value={config.branding.companyNameOverride}
                        onChange={(event) =>
                          setConfig({
                            ...config,
                            branding: {
                              ...config.branding,
                              companyNameOverride: event.target.value,
                            },
                          })
                        }
                        fullWidth
                      />

                      <TextField
                        label={t('branding.reportTitle')}
                        value={config.branding.reportTitle}
                        onChange={(event) =>
                          setConfig({
                            ...config,
                            branding: {
                              ...config.branding,
                              reportTitle: event.target.value,
                            },
                          })
                        }
                        fullWidth
                      />

                      <TextField
                        label={t('branding.productName')}
                        value={config.branding.productName}
                        onChange={(event) =>
                          setConfig({
                            ...config,
                            branding: {
                              ...config.branding,
                              productName: event.target.value,
                            },
                          })
                        }
                        fullWidth
                      />

                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth>
                            <InputLabel>{t('branding.language')}</InputLabel>
                            <Select
                              label={t('branding.language')}
                              value={config.language}
                              onChange={(event) =>
                                setConfig(
                                  applyReportLanguage(
                                    config,
                                    event.target
                                      .value as ReportConfig['language'],
                                    'manual'
                                  )
                                )
                              }
                            >
                              <MenuItem value="en">EN</MenuItem>
                              <MenuItem value="pl">PL</MenuItem>
                              <MenuItem value="sv">SV</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth>
                            <InputLabel>{t('branding.template')}</InputLabel>
                            <Select
                              label={t('branding.template')}
                              value={config.templateId}
                              onChange={(event) =>
                                setConfig({
                                  ...config,
                                  templateId: event.target
                                    .value as ReportConfig['templateId'],
                                })
                              }
                            >
                              <MenuItem value="modern">
                                {t('templates.modern')}
                              </MenuItem>
                              <MenuItem value="compact">
                                {t('templates.compact')}
                              </MenuItem>
                              <MenuItem value="audit-ready">
                                {t('templates.auditReady')}
                              </MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth>
                            <InputLabel>
                              {t('branding.accentIntensity')}
                            </InputLabel>
                            <Select
                              label={t('branding.accentIntensity')}
                              value={config.branding.accentIntensity}
                              onChange={(event) =>
                                setConfig({
                                  ...config,
                                  branding: {
                                    ...config.branding,
                                    accentIntensity: event.target
                                      .value as ReportConfig['branding']['accentIntensity'],
                                  },
                                })
                              }
                            >
                              <MenuItem value="low">
                                {t('branding.intensity.low')}
                              </MenuItem>
                              <MenuItem value="medium">
                                {t('branding.intensity.medium')}
                              </MenuItem>
                              <MenuItem value="high">
                                {t('branding.intensity.high')}
                              </MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <Stack spacing={1}>
                            <TextField
                              label={t('branding.accentColor')}
                              type="color"
                              value={
                                config.branding.accentColor ||
                                DEFAULT_REPORT_ACCENT_COLOR
                              }
                              onChange={(event) =>
                                setConfig({
                                  ...config,
                                  branding: {
                                    ...config.branding,
                                    accentColor: event.target.value,
                                  },
                                })
                              }
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                            />
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                              {ACCENT_COLOR_PRESETS.map((preset) => (
                                <Tooltip
                                  key={preset.value}
                                  title={t(preset.labelKey)}
                                >
                                  <IconButton
                                    onClick={() =>
                                      setConfig({
                                        ...config,
                                        branding: {
                                          ...config.branding,
                                          accentColor: preset.value,
                                        },
                                      })
                                    }
                                    sx={{
                                      width: 28,
                                      height: 28,
                                      border: '1px solid #d0d7de',
                                      backgroundColor: preset.value,
                                      borderRadius: '50%',
                                    }}
                                  />
                                </Tooltip>
                              ))}
                            </Stack>
                          </Stack>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth>
                            <InputLabel>{t('branding.coverStyle')}</InputLabel>
                            <Select
                              label={t('branding.coverStyle')}
                              value={config.branding.coverStyle}
                              onChange={(event) =>
                                setConfig({
                                  ...config,
                                  branding: {
                                    ...config.branding,
                                    coverStyle: event.target
                                      .value as ReportConfig['branding']['coverStyle'],
                                  },
                                })
                              }
                            >
                              <MenuItem value="none">
                                {t('branding.coverStyles.none')}
                              </MenuItem>
                              <MenuItem value="envirly">
                                {t('branding.coverStyles.envirly')}
                              </MenuItem>
                              <MenuItem value="user">
                                {t('branding.coverStyles.user')}
                              </MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>

                      <FormControlLabel
                        control={
                          <Switch
                            checked={config.branding.showDate}
                            onChange={(event) =>
                              setConfig({
                                ...config,
                                branding: {
                                  ...config.branding,
                                  showDate: event.target.checked,
                                },
                              })
                            }
                          />
                        }
                        label={t('branding.showDateOnCover')}
                      />
                    </Stack>
                  )}

                  {activeStep === 0 && isOnePager && (
                    <Stack spacing={2.5}>
                      <Typography variant="h4">
                        {t('reportTypes.lcaOnePager.label')}
                      </Typography>

                      <Alert severity="info">{t('onePager.lockedAlert')}</Alert>

                      <Card variant="outlined">
                        <CardContent>
                          <Stack spacing={1.5}>
                            <Typography variant="subtitle1">
                              {t('onePager.producerLogo')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {t('onePager.producerLogoDescription')}
                            </Typography>

                            <Box display="flex" gap={2} flexWrap="wrap">
                              <Button
                                variant="secondary"
                                component="label"
                                startIcon={<UploadFileOutlinedIcon />}
                                disabled={isUploading}
                              >
                                {t('branding.uploadLogo')}
                                <input
                                  hidden
                                  type="file"
                                  accept={LOGO_IMAGE_ACCEPT}
                                  onChange={(event) => {
                                    const file = event.target.files?.[0];
                                    if (file) onUploadImage(file, 'logo');
                                  }}
                                />
                              </Button>

                              <Tooltip
                                title={
                                  organization.logo
                                    ? ''
                                    : t('branding.noOrganizationLogo')
                                }
                              >
                                <span>
                                  <Button
                                    variant="secondary"
                                    startIcon={<SyncOutlinedIcon />}
                                    disabled={isUploading || !organization.logo}
                                    onClick={onUseOrganizationLogo}
                                  >
                                    {t('branding.useOrganizationLogo')}
                                  </Button>
                                </span>
                              </Tooltip>
                            </Box>

                            {config.branding.logoUrl && (
                              <Box
                                sx={{
                                  p: 2,
                                  border: '1px solid #ddd',
                                  borderRadius: 2,
                                  width: 'fit-content',
                                }}
                              >
                                <img
                                  src={config.branding.logoUrl}
                                  alt="logo"
                                  style={{
                                    maxWidth: 180,
                                    maxHeight: 70,
                                    objectFit: 'contain',
                                  }}
                                />
                              </Box>
                            )}
                          </Stack>
                        </CardContent>
                      </Card>

                      <Card variant="outlined">
                        <CardContent>
                          <Stack spacing={1.5}>
                            <Typography variant="subtitle1">
                              {t('onePager.automaticallyIncluded')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {t('onePager.automaticallyIncludedDescription')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {t('onePager.fixedOutputDescription')}
                            </Typography>
                            <TextField
                              label={t('branding.accentColor')}
                              type="color"
                              value={
                                config.branding.accentColor ||
                                DEFAULT_REPORT_ACCENT_COLOR
                              }
                              onChange={(event) =>
                                setConfig({
                                  ...config,
                                  branding: {
                                    ...config.branding,
                                    accentColor: event.target.value,
                                  },
                                })
                              }
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                            />
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                              {ACCENT_COLOR_PRESETS.map((preset) => (
                                <Tooltip
                                  key={preset.value}
                                  title={t(preset.labelKey)}
                                >
                                  <IconButton
                                    onClick={() =>
                                      setConfig({
                                        ...config,
                                        branding: {
                                          ...config.branding,
                                          accentColor: preset.value,
                                        },
                                      })
                                    }
                                    sx={{
                                      width: 28,
                                      height: 28,
                                      border: '1px solid #d0d7de',
                                      backgroundColor: preset.value,
                                      borderRadius: '50%',
                                    }}
                                  />
                                </Tooltip>
                              ))}
                            </Stack>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Stack>
                  )}

                  {activeStep === 1 && !isOnePager && (
                    <Stack spacing={2}>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography variant="h4">
                          {t('content.title')}
                        </Typography>
                        <Stack direction="row" spacing={1}>
                          <Button
                            variant="secondary"
                            size="small"
                            onClick={() => onSelectAllSections(true)}
                          >
                            {t('content.selectAll')}
                          </Button>
                          <Button
                            variant="secondary"
                            size="small"
                            onClick={() => onSelectAllSections(false)}
                          >
                            {t('content.clearAll')}
                          </Button>
                        </Stack>
                      </Box>

                      {REPORT_SECTIONS.map((section) => {
                        const configSection = getSection(config, section.id);
                        if (!configSection) return null;

                        const selectedImpactIds = getSelectedImpactIds(
                          configSection,
                          previewData.availableImpacts
                        );
                        const selectedImpacts = getSelectedImpacts(
                          configSection,
                          previewData.availableImpacts
                        );
                        const flowchartStageCount = getFlowchartStageSummaries(
                          diagramData,
                          previewData.selectedImpact
                        ).length;

                        return (
                          <Card key={section.id} variant="outlined">
                            <CardContent>
                              <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                gap={1.5}
                              >
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Typography variant="subtitle1">
                                    {getReportSectionTitle(
                                      section.id,
                                      uiContentLanguage
                                    )}
                                  </Typography>
                                  <Tooltip
                                    title={getReportSectionDescription(
                                      section.id,
                                      uiContentLanguage
                                    )}
                                  >
                                    <IconButton size="small">
                                      <InfoOutlinedIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                                <Switch
                                  checked={configSection.enabled}
                                  onChange={(event) =>
                                    onSectionToggle(
                                      section.id,
                                      event.target.checked
                                    )
                                  }
                                />
                              </Box>

                              <Typography
                                variant="body2"
                                color="text.secondary"
                                mt={0.5}
                              >
                                {getReportSectionDescription(
                                  section.id,
                                  uiContentLanguage
                                )}
                              </Typography>

                              {section.supportsDetailLevel && (
                                <Box mt={1.5} maxWidth={220}>
                                  <FormControl fullWidth size="small">
                                    <InputLabel>
                                      {t('content.detailLevel')}
                                    </InputLabel>
                                    <Select
                                      label={t('content.detailLevel')}
                                      value={configSection.detailLevel}
                                      onChange={(event) =>
                                        onSectionDetailLevel(
                                          section.id,
                                          event.target.value as DetailLevel
                                        )
                                      }
                                      disabled={!configSection.enabled}
                                    >
                                      <MenuItem value="summary">
                                        {t('detailLevels.summary')}
                                      </MenuItem>
                                      <MenuItem value="standard">
                                        {t('detailLevels.standard')}
                                      </MenuItem>
                                      <MenuItem value="detailed">
                                        {t('detailLevels.detailed')}
                                      </MenuItem>
                                    </Select>
                                  </FormControl>
                                </Box>
                              )}

                              {section.id === 'results-ghg' && (
                                <Stack spacing={1} mt={2}>
                                  <Typography variant="subtitle2">
                                    {t('content.resultsPresentation')}
                                  </Typography>
                                  <FormGroup row>
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={
                                            configSection.options
                                              .includeCharts !== false
                                          }
                                          disabled={!configSection.enabled}
                                          onChange={(event) =>
                                            onSectionOptionsChange(section.id, {
                                              includeCharts:
                                                event.target.checked,
                                            })
                                          }
                                        />
                                      }
                                      label={t(
                                        'content.showStageDistributionChart'
                                      )}
                                    />
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={
                                            configSection.options
                                              .includeHotspots !== false
                                          }
                                          disabled={!configSection.enabled}
                                          onChange={(event) =>
                                            onSectionOptionsChange(section.id, {
                                              includeHotspots:
                                                event.target.checked,
                                            })
                                          }
                                        />
                                      }
                                      label={t('content.showHotspotSummary')}
                                    />
                                  </FormGroup>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {t(
                                      'content.resultsPresentationDescription'
                                    )}
                                  </Typography>
                                </Stack>
                              )}

                              {section.id === 'system-boundaries' && (
                                <Stack spacing={1} mt={2}>
                                  <Typography variant="subtitle2">
                                    {t('content.flowchartSnapshot')}
                                  </Typography>
                                  <FormGroup row>
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={
                                            configSection.options
                                              .includeFlowchart !== false
                                          }
                                          disabled={!configSection.enabled}
                                          onChange={(event) =>
                                            onSectionOptionsChange(section.id, {
                                              includeFlowchart:
                                                event.target.checked,
                                            })
                                          }
                                        />
                                      }
                                      label={t(
                                        'content.includePlatformFlowchart'
                                      )}
                                    />
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={
                                            configSection.options
                                              .includeImpactValues !== false
                                          }
                                          disabled={!configSection.enabled}
                                          onChange={(event) =>
                                            onSectionOptionsChange(section.id, {
                                              includeImpactValues:
                                                event.target.checked,
                                            })
                                          }
                                        />
                                      }
                                      label={t(
                                        'content.showSelectedImpactValue'
                                      )}
                                    />
                                  </FormGroup>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {t('content.flowchartSnapshotDescription', {
                                      count: flowchartStageCount,
                                    })}
                                  </Typography>
                                </Stack>
                              )}

                              {section.id === 'impact-categories' && (
                                <Stack spacing={1.5} mt={2}>
                                  <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    gap={1}
                                    flexWrap="wrap"
                                  >
                                    <Typography variant="subtitle2">
                                      {t(
                                        'content.environmentalImpactsInReport'
                                      )}
                                    </Typography>
                                    <Stack direction="row" spacing={1}>
                                      <Button
                                        size="small"
                                        variant="secondary"
                                        disabled={!configSection.enabled}
                                        onClick={() =>
                                          onSectionOptionsChange(section.id, {
                                            selectedAreaIds:
                                              previewData.availableImpacts.map(
                                                (impact) => impact.area.id
                                              ),
                                          })
                                        }
                                      >
                                        {t('content.selectAllImpacts')}
                                      </Button>
                                      <Button
                                        size="small"
                                        variant="secondary"
                                        disabled={!configSection.enabled}
                                        onClick={() =>
                                          onSectionOptionsChange(section.id, {
                                            selectedAreaIds: [],
                                          })
                                        }
                                      >
                                        {t('content.clearImpacts')}
                                      </Button>
                                    </Stack>
                                  </Box>

                                  {!!previewData.availableImpacts.length && (
                                    <FormGroup>
                                      {previewData.availableImpacts.map(
                                        (impact) => (
                                          <FormControlLabel
                                            key={impact.area.id}
                                            control={
                                              <Checkbox
                                                checked={selectedImpactIds.includes(
                                                  impact.area.id
                                                )}
                                                disabled={
                                                  !configSection.enabled
                                                }
                                                onChange={(event) => {
                                                  const nextIds = event.target
                                                    .checked
                                                    ? [
                                                        ...selectedImpactIds,
                                                        impact.area.id,
                                                      ]
                                                    : selectedImpactIds.filter(
                                                        (id) =>
                                                          id !== impact.area.id
                                                      );

                                                  onSectionOptionsChange(
                                                    section.id,
                                                    {
                                                      selectedAreaIds: nextIds,
                                                    }
                                                  );
                                                }}
                                              />
                                            }
                                            label={`${impact.area.name} (${impact.area.unit_name})`}
                                          />
                                        )
                                      )}
                                    </FormGroup>
                                  )}

                                  {!previewData.availableImpacts.length && (
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      {t('content.noImpactCategories')}
                                    </Typography>
                                  )}

                                  <FormGroup row>
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={
                                            configSection.options
                                              .includeCharts !== false
                                          }
                                          disabled={!configSection.enabled}
                                          onChange={(event) =>
                                            onSectionOptionsChange(section.id, {
                                              includeCharts:
                                                event.target.checked,
                                            })
                                          }
                                        />
                                      }
                                      label={t(
                                        'content.showStageChartsForEachImpact'
                                      )}
                                    />
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={
                                            configSection.options
                                              .includeAdditionalEnvironmentalData !==
                                            false
                                          }
                                          disabled={!configSection.enabled}
                                          onChange={(event) =>
                                            onSectionOptionsChange(section.id, {
                                              includeAdditionalEnvironmentalData:
                                                event.target.checked,
                                            })
                                          }
                                        />
                                      }
                                      label={t(
                                        'content.includeAdditionalEnvironmentalDeclarations'
                                      )}
                                    />
                                  </FormGroup>

                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {t(
                                      'content.selectedImpactCategoriesCount',
                                      {
                                        count: selectedImpacts.length,
                                      }
                                    )}
                                  </Typography>
                                </Stack>
                              )}
                            </CardContent>
                          </Card>
                        );
                      })}
                    </Stack>
                  )}

                  {activeStep === 2 && !isOnePager && (
                    <Stack spacing={2}>
                      <Typography variant="h4">
                        {t('customContent.title')}
                      </Typography>
                      <Alert severity="info">
                        {t('customContent.description')}
                      </Alert>

                      {config.sections
                        .filter((section) => section.enabled)
                        .map((sectionConfig) => {
                          const customContent =
                            getSectionCustomContent(sectionConfig);
                          const customTextLength = htmlToPlainText(
                            customContent.html
                          ).length;
                          const hasCustomContent =
                            customTextLength > 0 ||
                            customContent.images.length > 0;

                          return (
                            <Accordion
                              key={sectionConfig.id}
                              defaultExpanded={
                                sectionConfig.id === 'executive-summary'
                              }
                            >
                              <AccordionSummary
                                expandIcon={<ExpandMoreOutlinedIcon />}
                              >
                                <Box
                                  display="flex"
                                  justifyContent="space-between"
                                  alignItems="center"
                                  width="100%"
                                  gap={2}
                                >
                                  <Box>
                                    <Typography variant="subtitle1">
                                      {getReportSectionTitle(
                                        sectionConfig.id,
                                        uiContentLanguage
                                      )}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      {getReportSectionDescription(
                                        sectionConfig.id,
                                        uiContentLanguage
                                      )}
                                    </Typography>
                                  </Box>
                                  <Chip
                                    size="small"
                                    color={
                                      hasCustomContent ? 'primary' : 'default'
                                    }
                                    label={
                                      hasCustomContent
                                        ? t('customContent.contentSummary', {
                                            textCount: customTextLength,
                                            imageCount:
                                              customContent.images.length,
                                          })
                                        : t('customContent.noCustomContent')
                                    }
                                  />
                                </Box>
                              </AccordionSummary>

                              <AccordionDetails>
                                <Stack spacing={2}>
                                  <Box>
                                    <Typography variant="subtitle2" mb={1}>
                                      {t('customContent.yourAdditionalText')}
                                    </Typography>
                                    <CustomRichTextEditor
                                      value={customContent.html}
                                      onChange={(html) =>
                                        onSectionCustomHtmlChange(
                                          sectionConfig.id,
                                          html
                                        )
                                      }
                                      placeholder={t(
                                        'customContent.placeholder',
                                        {
                                          section: getReportSectionTitle(
                                            sectionConfig.id,
                                            uiContentLanguage
                                          ),
                                        }
                                      )}
                                    />
                                  </Box>

                                  <Divider />

                                  <Box>
                                    <Box
                                      display="flex"
                                      justifyContent="space-between"
                                      alignItems="center"
                                      mb={1}
                                      gap={2}
                                      flexWrap="wrap"
                                    >
                                      <Box>
                                        <Typography variant="subtitle2">
                                          {t('customContent.images')}
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                        >
                                          {t('customContent.maxImages', {
                                            count: MAX_SECTION_IMAGES,
                                          })}
                                        </Typography>
                                      </Box>

                                      <Button
                                        variant="secondary"
                                        component="label"
                                        startIcon={<UploadFileOutlinedIcon />}
                                        disabled={
                                          isUploading ||
                                          customContent.images.length >=
                                            MAX_SECTION_IMAGES
                                        }
                                      >
                                        {t('customContent.uploadImage')}
                                        <input
                                          hidden
                                          type="file"
                                          accept="image/png,image/jpeg,image/webp,image/svg+xml"
                                          onChange={(event) => {
                                            const file =
                                              event.target.files?.[0];
                                            if (file) {
                                              onSectionImageUpload(
                                                sectionConfig.id,
                                                file
                                              );
                                            }
                                            event.target.value = '';
                                          }}
                                        />
                                      </Button>
                                    </Box>

                                    {!customContent.images.length && (
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                      >
                                        {t('customContent.noSectionImages')}
                                      </Typography>
                                    )}

                                    <Stack spacing={2}>
                                      {customContent.images.map(
                                        (image, imageIndex) => (
                                          <Card
                                            key={image.id}
                                            variant="outlined"
                                          >
                                            <CardContent>
                                              <Grid container spacing={2}>
                                                <Grid item xs={12} md={4}>
                                                  <Box
                                                    component="img"
                                                    src={image.url}
                                                    alt={
                                                      image.alt || image.name
                                                    }
                                                    sx={{
                                                      width: '100%',
                                                      maxHeight: 220,
                                                      objectFit: 'contain',
                                                      borderRadius: 1.5,
                                                      border: '1px solid',
                                                      borderColor: 'divider',
                                                      backgroundColor:
                                                        '#fafafa',
                                                    }}
                                                  />
                                                </Grid>

                                                <Grid item xs={12} md={8}>
                                                  <Stack spacing={1.5}>
                                                    <Box
                                                      display="flex"
                                                      justifyContent="space-between"
                                                      alignItems="center"
                                                      gap={1}
                                                    >
                                                      <Typography variant="subtitle2">
                                                        {image.name}
                                                      </Typography>
                                                      <Stack
                                                        direction="row"
                                                        spacing={1}
                                                      >
                                                        <IconButton
                                                          size="small"
                                                          disabled={
                                                            imageIndex === 0
                                                          }
                                                          onClick={() =>
                                                            onSectionImageMove(
                                                              sectionConfig.id,
                                                              image.id,
                                                              'up'
                                                            )
                                                          }
                                                        >
                                                          <ArrowUpwardOutlinedIcon fontSize="small" />
                                                        </IconButton>
                                                        <IconButton
                                                          size="small"
                                                          disabled={
                                                            imageIndex ===
                                                            customContent.images
                                                              .length -
                                                              1
                                                          }
                                                          onClick={() =>
                                                            onSectionImageMove(
                                                              sectionConfig.id,
                                                              image.id,
                                                              'down'
                                                            )
                                                          }
                                                        >
                                                          <ArrowDownwardOutlinedIcon fontSize="small" />
                                                        </IconButton>
                                                        <IconButton
                                                          size="small"
                                                          color="error"
                                                          onClick={() =>
                                                            onSectionImageDelete(
                                                              sectionConfig.id,
                                                              image.id
                                                            )
                                                          }
                                                        >
                                                          <DeleteOutlineOutlinedIcon fontSize="small" />
                                                        </IconButton>
                                                      </Stack>
                                                    </Box>

                                                    <TextField
                                                      label={t(
                                                        'customContent.caption'
                                                      )}
                                                      value={image.caption}
                                                      onChange={(event) =>
                                                        onSectionImageChange(
                                                          sectionConfig.id,
                                                          image.id,
                                                          {
                                                            caption:
                                                              event.target
                                                                .value,
                                                          }
                                                        )
                                                      }
                                                      fullWidth
                                                    />

                                                    <TextField
                                                      label={t(
                                                        'customContent.altText'
                                                      )}
                                                      value={image.alt}
                                                      onChange={(event) =>
                                                        onSectionImageChange(
                                                          sectionConfig.id,
                                                          image.id,
                                                          {
                                                            alt: event.target
                                                              .value,
                                                          }
                                                        )
                                                      }
                                                      fullWidth
                                                    />

                                                    <FormControl fullWidth>
                                                      <InputLabel>
                                                        {t(
                                                          'customContent.imageSize'
                                                        )}
                                                      </InputLabel>
                                                      <Select
                                                        label={t(
                                                          'customContent.imageSize'
                                                        )}
                                                        value={image.layout}
                                                        onChange={(event) =>
                                                          onSectionImageChange(
                                                            sectionConfig.id,
                                                            image.id,
                                                            {
                                                              layout: event
                                                                .target
                                                                .value as ReportSectionImageLayout,
                                                            }
                                                          )
                                                        }
                                                      >
                                                        <MenuItem value="small">
                                                          {t(
                                                            'customContent.imageSizes.small'
                                                          )}
                                                        </MenuItem>
                                                        <MenuItem value="medium">
                                                          {t(
                                                            'customContent.imageSizes.medium'
                                                          )}
                                                        </MenuItem>
                                                        <MenuItem value="full">
                                                          {t(
                                                            'customContent.imageSizes.full'
                                                          )}
                                                        </MenuItem>
                                                      </Select>
                                                    </FormControl>
                                                  </Stack>
                                                </Grid>
                                              </Grid>
                                            </CardContent>
                                          </Card>
                                        )
                                      )}
                                    </Stack>
                                  </Box>
                                </Stack>
                              </AccordionDetails>
                            </Accordion>
                          );
                        })}
                    </Stack>
                  )}

                  {activeStep === 3 && !isOnePager && (
                    <Stack spacing={2.5}>
                      <Typography variant="h4">{t('layout.title')}</Typography>

                      <FormControl>
                        <Typography variant="subtitle2" mb={1}>
                          {t('layout.audience')}
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          {[
                            {
                              id: 'client-ready',
                              label: t('audiences.clientReady'),
                            },
                            {
                              id: 'audit-ready',
                              label: t('audiences.auditReady'),
                            },
                            { id: 'internal', label: t('audiences.internal') },
                          ].map((audienceOption) => (
                            <Chip
                              key={audienceOption.id}
                              label={audienceOption.label}
                              color={
                                config.audience === audienceOption.id
                                  ? 'primary'
                                  : 'default'
                              }
                              variant={
                                config.audience === audienceOption.id
                                  ? 'filled'
                                  : 'outlined'
                              }
                              onClick={() =>
                                onAudienceChange(
                                  audienceOption.id as ReportConfig['audience']
                                )
                              }
                            />
                          ))}
                        </Stack>
                      </FormControl>

                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth>
                            <InputLabel>{t('layout.pageDensity')}</InputLabel>
                            <Select
                              label={t('layout.pageDensity')}
                              value={config.layout.density}
                              onChange={(event) =>
                                setConfig({
                                  ...config,
                                  layout: {
                                    ...config.layout,
                                    density: event.target
                                      .value as ReportConfig['layout']['density'],
                                  },
                                })
                              }
                            >
                              <MenuItem value="spacious">
                                {t('layout.density.spacious')}
                              </MenuItem>
                              <MenuItem value="standard">
                                {t('layout.density.standard')}
                              </MenuItem>
                              <MenuItem value="compact">
                                {t('layout.density.compact')}
                              </MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth>
                            <InputLabel>{t('layout.chartStyle')}</InputLabel>
                            <Select
                              label={t('layout.chartStyle')}
                              value={config.layout.chartStyle}
                              onChange={(event) =>
                                setConfig({
                                  ...config,
                                  layout: {
                                    ...config.layout,
                                    chartStyle: event.target
                                      .value as ReportConfig['layout']['chartStyle'],
                                  },
                                })
                              }
                            >
                              <MenuItem value="donut-bars">
                                {t('layout.chartStyles.donutBars')}
                              </MenuItem>
                              <MenuItem value="bars-only">
                                {t('layout.chartStyles.barsOnly')}
                              </MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={4}>
                          <TextField
                            label={t('layout.decimals')}
                            type="number"
                            value={config.layout.numberFormat.decimals}
                            onChange={(event) =>
                              setConfig({
                                ...config,
                                layout: {
                                  ...config.layout,
                                  numberFormat: {
                                    ...config.layout.numberFormat,
                                    decimals: Math.min(
                                      4,
                                      Math.max(
                                        0,
                                        Number(event.target.value || 0)
                                      )
                                    ),
                                  },
                                },
                              })
                            }
                            fullWidth
                          />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                          <FormControl fullWidth>
                            <InputLabel>
                              {t('layout.thousandsSeparator')}
                            </InputLabel>
                            <Select
                              label={t('layout.thousandsSeparator')}
                              value={
                                config.layout.numberFormat.thousandsSeparator
                              }
                              onChange={(event) =>
                                setConfig({
                                  ...config,
                                  layout: {
                                    ...config.layout,
                                    numberFormat: {
                                      ...config.layout.numberFormat,
                                      thousandsSeparator: event.target
                                        .value as ReportConfig['layout']['numberFormat']['thousandsSeparator'],
                                    },
                                  },
                                })
                              }
                            >
                              <MenuItem value=",">
                                {t('layout.separators.comma')}
                              </MenuItem>
                              <MenuItem value=" ">
                                {t('layout.separators.space')}
                              </MenuItem>
                              <MenuItem value=".">
                                {t('layout.separators.dot')}
                              </MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={4}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={config.layout.numberFormat.showUnits}
                                onChange={(event) =>
                                  setConfig({
                                    ...config,
                                    layout: {
                                      ...config.layout,
                                      numberFormat: {
                                        ...config.layout.numberFormat,
                                        showUnits: event.target.checked,
                                      },
                                    },
                                  })
                                }
                              />
                            }
                            label={t('layout.showUnits')}
                          />
                        </Grid>
                      </Grid>

                      <Divider />

                      <Typography variant="subtitle2">
                        {t('layout.footerOptions')}
                      </Typography>
                      <FormGroup row>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={
                                config.layout.footerOptions.showContactInfo
                              }
                              onChange={(event) =>
                                setConfig({
                                  ...config,
                                  layout: {
                                    ...config.layout,
                                    footerOptions: {
                                      ...config.layout.footerOptions,
                                      showContactInfo: event.target.checked,
                                    },
                                  },
                                })
                              }
                            />
                          }
                          label={t('layout.contactInfo')}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={config.layout.footerOptions.showWebsite}
                              onChange={(event) =>
                                setConfig({
                                  ...config,
                                  layout: {
                                    ...config.layout,
                                    footerOptions: {
                                      ...config.layout.footerOptions,
                                      showWebsite: event.target.checked,
                                    },
                                  },
                                })
                              }
                            />
                          }
                          label={t('layout.website')}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={
                                config.layout.footerOptions.showPageNumbers
                              }
                              onChange={(event) =>
                                setConfig({
                                  ...config,
                                  layout: {
                                    ...config.layout,
                                    footerOptions: {
                                      ...config.layout.footerOptions,
                                      showPageNumbers: event.target.checked,
                                    },
                                  },
                                })
                              }
                            />
                          }
                          label={t('layout.pageNumbers')}
                        />
                      </FormGroup>

                      <FormControlLabel
                        control={
                          <Switch
                            checked={config.layout.watermark.enabled}
                            onChange={(event) =>
                              setConfig({
                                ...config,
                                layout: {
                                  ...config.layout,
                                  watermark: {
                                    ...config.layout.watermark,
                                    enabled: event.target.checked,
                                  },
                                },
                              })
                            }
                          />
                        }
                        label={t('layout.confidentialWatermark')}
                      />
                    </Stack>
                  )}

                  {activeStep === (isOnePager ? 1 : 4) && (
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={3.5}>
                        <Card
                          variant="outlined"
                          sx={{ position: 'sticky', top: 16 }}
                        >
                          <CardContent>
                            <Typography variant="subtitle1">
                              {t('preview.sections')}
                            </Typography>
                            <List dense>
                              {sectionPreviews.map((sectionPreview) => (
                                <ListItemButton
                                  key={sectionPreview.id}
                                  onClick={() => {
                                    sectionRefs.current[
                                      sectionPreview.id
                                    ]?.scrollIntoView({
                                      behavior: 'smooth',
                                      block: 'start',
                                    });
                                  }}
                                >
                                  <ListItemText
                                    primary={sectionPreview.title}
                                    secondary={t('preview.page', {
                                      page: sectionPreview.page,
                                    })}
                                  />
                                </ListItemButton>
                              ))}
                            </List>
                          </CardContent>
                        </Card>
                      </Grid>

                      <Grid item xs={12} md={8.5}>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={2}
                        >
                          <Typography variant="h4">
                            {isOnePager
                              ? t('preview.onePagerPreview')
                              : t('preview.livePreview')}
                          </Typography>
                          {!isOnePager && (
                            <Stack direction="row" spacing={1}>
                              <FormControl size="small" sx={{ minWidth: 220 }}>
                                <InputLabel>
                                  {t('preview.impactArea')}
                                </InputLabel>
                                <Select
                                  label={t('preview.impactArea')}
                                  value={selectedAreaId || ''}
                                  onChange={(event) =>
                                    setSelectedAreaId(
                                      event.target.value
                                        ? Number(event.target.value)
                                        : undefined
                                    )
                                  }
                                >
                                  {previewData.availableAreas.map((area) => (
                                    <MenuItem key={area.id} value={area.id}>
                                      {area.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                              <Button
                                variant="secondary"
                                startIcon={<DownloadOutlinedIcon />}
                                onClick={onDownloadDraft}
                              >
                                {t('preview.downloadDraft')}
                              </Button>
                            </Stack>
                          )}
                        </Box>

                        <Stack spacing={1.5} mb={2}>
                          {validationIssues.map((issue) => (
                            <Alert
                              key={issue.id}
                              severity={issue.blocking ? 'error' : 'warning'}
                              icon={<WarningAmberOutlinedIcon />}
                            >
                              {issue.message}
                            </Alert>
                          ))}
                        </Stack>

                        <Stack spacing={2}>
                          {sectionPreviews.map((sectionPreview) => {
                            const sectionWarnings = validationIssues.filter(
                              (issue) => issue.sectionId === sectionPreview.id
                            );
                            const sectionConfig = getSection(
                              config,
                              sectionPreview.id
                            );
                            const customContent =
                              getSectionCustomContent(sectionConfig);
                            const hasCustomContent =
                              htmlToPlainText(customContent.html).length > 0 ||
                              customContent.images.length > 0;

                            return (
                              <Card
                                key={sectionPreview.id}
                                variant="outlined"
                                ref={(element) => {
                                  sectionRefs.current[sectionPreview.id] =
                                    element;
                                }}
                              >
                                <CardContent>
                                  <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    mb={1}
                                  >
                                    <Typography variant="h5">
                                      {sectionPreview.title}
                                    </Typography>
                                    <Chip
                                      label={t('preview.pageChip', {
                                        page: sectionPreview.page,
                                      })}
                                      size="small"
                                      variant="outlined"
                                    />
                                  </Box>

                                  {isOnePager && (
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      {t('onePager.previewDescription')}
                                    </Typography>
                                  )}

                                  {sectionPreview.id === 'executive-summary' &&
                                    !isOnePager && (
                                      <>
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                        >
                                          {t('preview.totalFootprint')}
                                        </Typography>
                                        <Typography
                                          variant="h5"
                                          color="primary.main"
                                        >
                                          {formatNumber(
                                            previewData.totalFootprint,
                                            config.layout.numberFormat
                                          )}{' '}
                                          {config.layout.numberFormat.showUnits
                                            ? t('preview.units.kgCo2e')
                                            : ''}
                                        </Typography>
                                        <Typography variant="body2" mt={1}>
                                          {buildAutoInterpretation(
                                            previewData,
                                            config.language
                                          )}
                                        </Typography>
                                      </>
                                    )}

                                  {sectionPreview.id ===
                                    'product-description' && (
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      {t(
                                        'preview.productDescription.functionalUnit'
                                      )}{' '}
                                      {String(
                                        product.functional_unit ||
                                          t('common.notProvided')
                                      )}
                                      {' | '}
                                      {t(
                                        'preview.productDescription.weight'
                                      )}{' '}
                                      {String(
                                        product.weight ||
                                          t('common.notProvided')
                                      )}
                                      {' | '}
                                      {t(
                                        'preview.productDescription.dimensions'
                                      )}{' '}
                                      {String(
                                        product.dimensions ||
                                          t('common.notProvided')
                                      )}
                                    </Typography>
                                  )}

                                  {sectionPreview.id ===
                                    'methodology-standards' && (
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      {t('preview.methodology.boundary')}{' '}
                                      {getLifecycleBoundaryLabel(
                                        product.lifecycle_boundaries
                                      )}
                                      {' | '}
                                      {t('preview.methodology.goals')}{' '}
                                      {hasMethodologyData(product)
                                        ? t('preview.methodology.connected')
                                        : t('preview.methodology.limited')}
                                    </Typography>
                                  )}

                                  {sectionPreview.id ===
                                    'materials-circularity' && (
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      {t('preview.materials.materials')}{' '}
                                      {product.materials.length ||
                                        t('common.notProvided')}
                                      {' | '}
                                      {t(
                                        'preview.materials.weightedRecycledShare'
                                      )}{' '}
                                      {product.materials.length
                                        ? `${getWeightedRecycledShare(
                                            product
                                          ).toFixed(1)}%`
                                        : t('common.notProvided')}
                                      {' | '}
                                      {t(
                                        'preview.materials.circularityNotes'
                                      )}{' '}
                                      {hasMaterialsCircularityData(product)
                                        ? t('preview.common.available')
                                        : t('preview.common.limited')}
                                    </Typography>
                                  )}

                                  {sectionPreview.id ===
                                    'durability-end-of-life' && (
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      {t('preview.durability.serviceLife')}{' '}
                                      {String(
                                        product.reference_service_life ||
                                          product.guaranteed_service_life ||
                                          t('common.notProvided')
                                      )}
                                      {' | '}
                                      {t(
                                        'preview.durability.repairability'
                                      )}{' '}
                                      {hasDurabilityData(product)
                                        ? t('preview.common.available')
                                        : t('preview.common.limited')}
                                    </Typography>
                                  )}

                                  {sectionPreview.id ===
                                    'data-quality-readiness' && (
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      {readinessMetrics
                                        .map(
                                          (metric) =>
                                            `${metric.label}: ${Math.round(
                                              metric.percentage
                                            )}%`
                                        )
                                        .join(' | ')}
                                    </Typography>
                                  )}

                                  {sectionPreview.id === 'results-ghg' &&
                                    !previewData.selectedImpact && (
                                      <Alert severity="info" sx={{ mt: 1 }}>
                                        {t('common.noData')}
                                      </Alert>
                                    )}

                                  {sectionPreview.id === 'results-ghg' &&
                                    previewData.selectedImpact && (
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        mt={1}
                                      >
                                        {t('preview.results.impactArea')}{' '}
                                        {previewData.selectedImpact.area.name}
                                        {' | '}
                                        {t('preview.results.chart')}{' '}
                                        {sectionConfig?.options
                                          .includeCharts !== false
                                          ? t('preview.common.included')
                                          : t('preview.common.hidden')}
                                        {' | '}
                                        {t('preview.results.hotspots')}{' '}
                                        {sectionConfig?.options
                                          .includeHotspots !== false
                                          ? t('preview.common.included')
                                          : t('preview.common.hidden')}
                                      </Typography>
                                    )}

                                  {sectionPreview.id ===
                                    'system-boundaries' && (
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      {t('preview.systemBoundaries.boundary')}{' '}
                                      {getLifecycleBoundaryLabel(
                                        product.lifecycle_boundaries
                                      )}
                                      {' | '}
                                      {t(
                                        'preview.systemBoundaries.flowchartSnapshot'
                                      )}{' '}
                                      {sectionConfig?.options
                                        .includeFlowchart !== false
                                        ? t('preview.common.included')
                                        : t('preview.common.hidden')}
                                      {' | '}
                                      {t(
                                        'preview.systemBoundaries.stageCards'
                                      )}{' '}
                                      {
                                        getFlowchartStageSummaries(
                                          diagramData,
                                          previewData.selectedImpact
                                        ).length
                                      }
                                    </Typography>
                                  )}

                                  {sectionPreview.id ===
                                    'impact-categories' && (
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      {t(
                                        'preview.impactCategories.selectedImpacts'
                                      )}{' '}
                                      {getSelectedImpacts(
                                        sectionConfig,
                                        previewData.availableImpacts
                                      )
                                        .map((impact) => impact.area.name)
                                        .slice(0, 4)
                                        .join(', ') ||
                                        t('preview.impactCategories.none')}
                                      {getSelectedImpacts(
                                        sectionConfig,
                                        previewData.availableImpacts
                                      ).length > 4
                                        ? '...'
                                        : ''}
                                    </Typography>
                                  )}

                                  {hasCustomContent && (
                                    <Box
                                      mt={2}
                                      pt={2}
                                      sx={{
                                        borderTop: '1px solid',
                                        borderColor: 'divider',
                                      }}
                                    >
                                      <Typography variant="subtitle2" mb={1}>
                                        {t('preview.additionalUserContent')}
                                      </Typography>

                                      {!!htmlToPlainText(customContent.html)
                                        .length && (
                                        <Box
                                          sx={{
                                            '& p, & div': {
                                              mt: 0,
                                              mb: 1,
                                            },
                                            '& h2': {
                                              mt: 0,
                                              mb: 1,
                                              color: 'primary.main',
                                            },
                                            '& ul, & ol': {
                                              mt: 0,
                                              mb: 1,
                                              pl: 3,
                                            },
                                            '& blockquote': {
                                              m: 0,
                                              mb: 1,
                                              pl: 1.5,
                                              borderLeft: '3px solid',
                                              borderColor: 'primary.light',
                                              color: 'text.secondary',
                                            },
                                            '& a': {
                                              color: 'primary.main',
                                            },
                                          }}
                                          dangerouslySetInnerHTML={{
                                            __html: sanitizeRichTextHtml(
                                              customContent.html
                                            ),
                                          }}
                                        />
                                      )}

                                      {!!customContent.images.length && (
                                        <Box
                                          display="flex"
                                          flexWrap="wrap"
                                          gap={2}
                                          mt={1.5}
                                        >
                                          {customContent.images.map((image) => (
                                            <Box
                                              key={image.id}
                                              sx={{
                                                width: getSectionImageWidth(
                                                  image.layout
                                                ),
                                                minWidth:
                                                  getSectionImageMinWidth(
                                                    image.layout
                                                  ),
                                              }}
                                            >
                                              <Box
                                                component="img"
                                                src={image.url}
                                                alt={image.alt || image.name}
                                                sx={{
                                                  width: '100%',
                                                  maxHeight: 260,
                                                  objectFit: 'contain',
                                                  borderRadius: 1.5,
                                                  border: '1px solid',
                                                  borderColor: 'divider',
                                                  backgroundColor: '#fafafa',
                                                }}
                                              />
                                              {(image.caption || image.alt) && (
                                                <Typography
                                                  variant="caption"
                                                  color="text.secondary"
                                                  display="block"
                                                  mt={0.75}
                                                >
                                                  {image.caption || image.alt}
                                                </Typography>
                                              )}
                                            </Box>
                                          ))}
                                        </Box>
                                      )}
                                    </Box>
                                  )}

                                  {sectionWarnings.map((warning) => (
                                    <Alert
                                      key={warning.id}
                                      severity="warning"
                                      sx={{ mt: 1 }}
                                    >
                                      {warning.message}
                                    </Alert>
                                  ))}
                                </CardContent>
                              </Card>
                            );
                          })}
                        </Stack>
                      </Grid>
                    </Grid>
                  )}

                  {activeStep === (isOnePager ? 2 : 5) && (
                    <Stack spacing={2.5}>
                      <Typography variant="h4">
                        {t('generate.title')}
                      </Typography>

                      {!isOnePager ? (
                        <TextField
                          label={t('generate.reportName')}
                          value={config.output.reportName}
                          onChange={(event) =>
                            setConfig({
                              ...config,
                              output: {
                                ...config.output,
                                reportName: event.target.value,
                              },
                            })
                          }
                          fullWidth
                        />
                      ) : (
                        <Alert severity="info">
                          {t('generate.onePagerInfo')}
                        </Alert>
                      )}

                      {!isOnePager ? (
                        <FormGroup row>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={config.output.formats.includes('pdf')}
                                onChange={(event) =>
                                  onChangeFormats('pdf', event.target.checked)
                                }
                              />
                            }
                            label={t('generate.formats.pdfRequired')}
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={config.output.formats.includes('docx')}
                                onChange={(event) =>
                                  onChangeFormats('docx', event.target.checked)
                                }
                              />
                            }
                            label={t('generate.formats.docxOptional')}
                          />
                        </FormGroup>
                      ) : (
                        <Alert severity="info">
                          {t('generate.pdfOnlyInfo')}
                        </Alert>
                      )}

                      {!isOnePager && (
                        <FormControl>
                          <Typography variant="subtitle2">
                            {t('generate.saveAs')}
                          </Typography>
                          <RadioGroup
                            value={saveMode}
                            onChange={(event) =>
                              setSaveMode(event.target.value as SaveMode)
                            }
                          >
                            <FormControlLabel
                              value="new-version"
                              control={<Radio />}
                              label={t('generate.newVersion')}
                            />
                            <FormControlLabel
                              value="new-report"
                              control={<Radio />}
                              label={t('generate.newReport')}
                            />
                          </RadioGroup>
                        </FormControl>
                      )}

                      {isGenerating && (
                        <Card variant="outlined">
                          <CardContent>
                            <Typography variant="subtitle1" mb={1}>
                              {generationStages[generationStage]}
                            </Typography>
                            <LinearProgress
                              variant="determinate"
                              value={
                                ((generationStage + 1) /
                                  generationStages.length) *
                                100
                              }
                            />
                          </CardContent>
                        </Card>
                      )}

                      {successRecord && (
                        <Alert
                          severity="success"
                          icon={<CheckCircleOutlineIcon />}
                          action={
                            <Stack direction="row" spacing={1}>
                              <Button
                                size="small"
                                variant="secondary"
                                onClick={() => setPageTab('generated-reports')}
                              >
                                {t('generate.viewHistory')}
                              </Button>
                              <Button
                                size="small"
                                variant="secondary"
                                onClick={onShare}
                                startIcon={<ShareOutlinedIcon />}
                              >
                                {t('generate.shareLink')}
                              </Button>
                            </Stack>
                          }
                        >
                          {t('generate.success', {
                            versionLabel: getVersionLabel(successRecord),
                          })}
                        </Alert>
                      )}

                      <Button
                        variant="primary"
                        onClick={onGenerate}
                        disabled={isGenerating || hasBlockingValidation}
                      >
                        {t('actions.generate')}
                      </Button>

                      {!!history.length && (
                        <Card variant="outlined">
                          <CardContent>
                            <Typography variant="subtitle1" mb={1}>
                              {t('generate.versionHistory')}
                            </Typography>
                            <Stack spacing={1}>
                              {history.slice(0, 10).map((entry) => (
                                <Box
                                  key={entry.reportId}
                                  display="flex"
                                  justifyContent="space-between"
                                  alignItems="center"
                                  p={1}
                                  sx={{
                                    border: '1px solid #eee',
                                    borderRadius: 1.5,
                                  }}
                                >
                                  <Box>
                                    <Typography
                                      variant="body2"
                                      fontWeight={600}
                                    >
                                      {entry.reportName}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      {getVersionLabel(entry)} ·{' '}
                                      {entry.formats.join(', ').toUpperCase()}
                                    </Typography>
                                  </Box>
                                  <Chip
                                    size="small"
                                    label={getWarningsLabel(
                                      entry.warningsCount
                                    )}
                                  />
                                </Box>
                              ))}
                            </Stack>
                          </CardContent>
                        </Card>
                      )}
                    </Stack>
                  )}

                  <Divider sx={{ my: 2.5 }} />

                  <Box display="flex" justifyContent="space-between">
                    <Button
                      variant="secondary"
                      onClick={() =>
                        setActiveStep((step) => Math.max(0, step - 1))
                      }
                      disabled={activeStep === 0}
                    >
                      {t('actions.back')}
                    </Button>

                    <Button
                      variant="primary"
                      onClick={() =>
                        setActiveStep((step) =>
                          Math.min(lastStepIndex, step + 1)
                        )
                      }
                      disabled={activeStep === lastStepIndex}
                    >
                      {t('actions.next')}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3} lg={2.6}>
              <Card sx={{ position: 'sticky', top: 16 }}>
                <CardContent>
                  <Typography variant="h5" mb={1}>
                    {t('summary.title')}
                  </Typography>
                  <Stack spacing={1.2}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t('summary.template')}
                      </Typography>
                      <Typography variant="body2">
                        {t(
                          `templates.${config.templateId === 'audit-ready' ? 'auditReady' : config.templateId}`
                        )}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t('summary.reportType')}
                      </Typography>
                      <Typography variant="body2">
                        {getReportTypeLabel(
                          config.reportType,
                          uiContentLanguage
                        )}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t('summary.enabledSections')}
                      </Typography>
                      <Typography variant="body2">
                        {enabledSectionsCount}/{REPORT_SECTIONS.length}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t('summary.estimatedPages')}
                      </Typography>
                      <Typography variant="body2">{estimatedPages}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t('summary.warnings')}
                      </Typography>
                      <Typography variant="body2">
                        {
                          validationIssues.filter((issue) => !issue.blocking)
                            .length
                        }
                      </Typography>
                    </Box>
                  </Stack>

                  <Divider sx={{ my: 1.5 }} />

                  <Tooltip
                    title={
                      hasBlockingValidation
                        ? t('toasts.resolveBlockingIssues')
                        : ''
                    }
                  >
                    <span>
                      <Button
                        fullWidth
                        variant="primary"
                        onClick={() => {
                          if (activeStep !== lastStepIndex) {
                            setActiveStep(lastStepIndex);
                          } else {
                            onGenerate();
                          }
                        }}
                        disabled={isGenerating || hasBlockingValidation}
                      >
                        {activeStep === lastStepIndex
                          ? t('actions.generate')
                          : t('actions.goToGenerate')}
                      </Button>
                    </span>
                  </Tooltip>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      ) : (
        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="h4">
                  {t('tabs.generatedReports')}
                </Typography>
                <Button
                  variant="secondary"
                  onClick={() => setPageTab('generate')}
                >
                  {t('actions.backToBuilder')}
                </Button>
              </Box>

              <Alert severity="info">{t('history.sharedInfo')}</Alert>

              {generatedReportsContent}
            </Stack>
          </CardContent>
        </Card>
      )}
    </LcaLayout>
  );
}
